"use client"

import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority"
import { PanelLeftIcon } from "lucide-react"

import { cn } from "../../lib/utils"
import { Button } from "./button"
import { Input } from "./input"
import { Separator } from "./separator"
import {
  Sheet,
  SheetContent,
} from "./sheet"
import { useTheme } from '../../contexts/ThemeContext'

const SIDEBAR_COOKIE_NAME = "sidebar_state"
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7
const SIDEBAR_WIDTH = "16rem"
const SIDEBAR_WIDTH_MOBILE = "18rem"
const SIDEBAR_WIDTH_ICON = "3rem"
const SIDEBAR_KEYBOARD_SHORTCUT = "b"

const SidebarContext = React.createContext(null)

function useSidebar() {
  const context = React.useContext(SidebarContext)
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.")
  }

  return context
}

// Hook para detectar si es móvil
function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(false)

  React.useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    
    checkIsMobile()
    window.addEventListener('resize', checkIsMobile)
    
    return () => window.removeEventListener('resize', checkIsMobile)
  }, [])

  return isMobile
}

function SidebarProvider({
  defaultOpen = true,
  open: openProp,
  onOpenChange: setOpenProp,
  className,
  style,
  children,
  ...props
}) {
  const { themeObject } = useTheme();
  const wrapperTokens = themeObject.sidebarTokens?.wrapper || {};
  
  const isMobile = useIsMobile()
  const [openMobile, setOpenMobile] = React.useState(false)

  const [_open, _setOpen] = React.useState(defaultOpen)
  const open = openProp ?? _open
  const setOpen = React.useCallback(
    (value) => {
      const openState = typeof value === "function" ? value(open) : value
      if (setOpenProp) {
        setOpenProp(openState)
      } else {
        _setOpen(openState)
      }

      document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`
    },
    [setOpenProp, open]
  )

  const toggleSidebar = React.useCallback(() => {
    return isMobile ? setOpenMobile((open) => !open) : setOpen((open) => !open)
  }, [isMobile, setOpen, setOpenMobile])

  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        event.key === SIDEBAR_KEYBOARD_SHORTCUT &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault()
        toggleSidebar()
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [toggleSidebar])

  const state = open ? "expanded" : "collapsed"

  const contextValue = React.useMemo(
    () => ({
      state,
      open,
      setOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
    }),
    [state, open, setOpen, isMobile, openMobile, setOpenMobile, toggleSidebar]
  )

  return (
    <SidebarContext.Provider value={contextValue}>
      <div
        style={{
          "--sidebar-width": SIDEBAR_WIDTH,
          "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
          display: wrapperTokens.display,
          minHeight: wrapperTokens.minHeight,
          width: wrapperTokens.width,
          ...style,
        }}
        className={cn(
          "group/sidebar-wrapper flex min-h-svh w-full",
          className
        )}
        {...props}
      >
        {children}
      </div>
    </SidebarContext.Provider>
  )
}

function Sidebar({
  side = "left",
  variant = "sidebar",
  collapsible = "offcanvas",
  className,
  children,
  ...props
}) {
  const { themeObject } = useTheme();
  const containerTokens = themeObject.sidebarTokens?.container || {};
  
  const { isMobile, state, openMobile, setOpenMobile } = useSidebar()

  if (collapsible === "none") {
    return (
      <div
        className={cn(
          "bg-sidebar text-sidebar-foreground flex h-full w-64 flex-col",
          className
        )}
        style={{
          backgroundColor: containerTokens.backgroundColor,
          color: containerTokens.color,
          display: containerTokens.display,
          height: containerTokens.height,
          width: containerTokens.width,
          flexDirection: containerTokens.flexDirection,
        }}
        {...props}
      >
        {children}
      </div>
    )
  }

  if (isMobile) {
    return (
      <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
        <SheetContent
          className="w-64 p-0 [&>button]:hidden bg-sidebar text-sidebar-foreground"
          style={{
            "--sidebar-width": SIDEBAR_WIDTH_MOBILE,
            width: containerTokens.width,
            padding: containerTokens.padding,
          }}
          side={side}
        >
          <div 
            className="flex h-full w-full flex-col"
            style={{
              display: containerTokens.display,
              height: containerTokens.height,
              width: '100%',
              flexDirection: containerTokens.flexDirection,
            }}
          >
            {children}
          </div>
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <div
      className="group peer text-gray-900 dark:text-gray-100 hidden md:block"
      data-state={state}
      data-collapsible={state === "collapsed" ? collapsible : ""}
      data-variant={variant}
      data-side={side}
    >
      <div
        className={cn(
          "relative w-64 bg-transparent transition-[width] duration-200 ease-linear",
          "group-data-[collapsible=offcanvas]:w-0",
          "group-data-[side=right]:rotate-180",
          variant === "floating" || variant === "inset"
            ? "group-data-[collapsible=icon]:w-16"
            : "group-data-[collapsible=icon]:w-12"
        )}
      />
      <div
        className={cn(
          "fixed inset-y-0 z-10 hidden h-svh w-64 transition-[left,right,width] duration-200 ease-linear md:flex",
          side === "left"
            ? "left-0 group-data-[collapsible=offcanvas]:left-[-16rem]"
            : "right-0 group-data-[collapsible=offcanvas]:right-[-16rem]",
          variant === "floating" || variant === "inset"
            ? "p-2 group-data-[collapsible=icon]:w-16"
            : "group-data-[collapsible=icon]:w-12 group-data-[side=left]:border-r group-data-[side=right]:border-l",
          className
        )}
        {...props}
      >
        <div
          className="flex h-full w-full flex-col group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:shadow-sm bg-sidebar text-sidebar-foreground border-sidebar-border"
          style={{
            display: containerTokens.display,
            height: containerTokens.height,
            width: '100%',
            flexDirection: containerTokens.flexDirection,
          }}
        >
          {children}
        </div>
      </div>
    </div>
  )
}

function SidebarTrigger({
  className,
  onClick,
  ...props
}) {
  const { themeObject } = useTheme();
  const triggerTokens = themeObject.sidebarTokens?.trigger || {};
  
  const { toggleSidebar } = useSidebar()

  return (
    <Button
      variant="ghost"
      size="icon"
      className={cn("h-7 w-7", className)}
      style={{
        height: triggerTokens.height,
        width: triggerTokens.width,
      }}
      onClick={(event) => {
        onClick?.(event)
        toggleSidebar()
      }}
      {...props}
    >
      <PanelLeftIcon className="h-4 w-4" />
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  )
}

function SidebarInset({ className, ...props }) {
  const { themeObject } = useTheme();
  const insetTokens = themeObject.sidebarTokens?.inset || {};
  
  return (
    <main
      className={cn(
        "bg-background relative flex w-full flex-1 flex-col",
        className
      )}
      style={{
        backgroundColor: insetTokens.backgroundColor,
        position: insetTokens.position,
        display: insetTokens.display,
        width: insetTokens.width,
        flex: insetTokens.flex,
        flexDirection: insetTokens.flexDirection,
      }}
      {...props}
    />
  )
}

function SidebarInput({ className, ...props }) {
  const { themeObject } = useTheme();
  const inputTokens = themeObject.sidebarTokens?.input || {};
  
  return (
    <Input
      className={cn("bg-background h-8 w-full shadow-none", className)}
      style={{
        backgroundColor: inputTokens.backgroundColor,
        height: inputTokens.height,
        width: inputTokens.width,
        boxShadow: inputTokens.boxShadow,
      }}
      {...props}
    />
  )
}

function SidebarHeader({ className, ...props }) {
  const { themeObject } = useTheme();
  const headerTokens = themeObject.sidebarTokens?.header || {};
  
  return (
    <div
      className={cn("flex flex-col gap-2 p-2", className)}
      style={{
        display: headerTokens.display,
        flexDirection: headerTokens.flexDirection,
        gap: headerTokens.gap,
        padding: headerTokens.padding,
      }}
      {...props}
    />
  )
}

function SidebarFooter({ className, ...props }) {
  const { themeObject } = useTheme();
  const footerTokens = themeObject.sidebarTokens?.footer || {};
  
  return (
    <div
      className={cn("flex flex-col gap-2 p-2", className)}
      style={{
        display: footerTokens.display,
        flexDirection: footerTokens.flexDirection,
        gap: footerTokens.gap,
        padding: footerTokens.padding,
      }}
      {...props}
    />
  )
}

function SidebarSeparator({ className, ...props }) {
  const { themeObject } = useTheme();
  const separatorTokens = themeObject.sidebarTokens?.separator || {};
  
  return (
    <Separator
      className={cn("bg-gray-200 dark:bg-gray-700 mx-2 w-auto", className)}
      style={{
        marginLeft: separatorTokens.marginLeft,
        marginRight: separatorTokens.marginRight,
        width: separatorTokens.width,
      }}
      {...props}
    />
  )
}

function SidebarContent({ className, ...props }) {
  const { themeObject } = useTheme();
  const contentTokens = themeObject.sidebarTokens?.content || {};
  
  return (
    <div
      className={cn(
        "flex min-h-0 flex-1 flex-col gap-2 overflow-auto group-data-[collapsible=icon]:overflow-hidden",
        className
      )}
      style={{
        display: contentTokens.display,
        minHeight: contentTokens.minHeight,
        flex: contentTokens.flex,
        flexDirection: contentTokens.flexDirection,
        gap: contentTokens.gap,
        overflow: contentTokens.overflow,
      }}
      {...props}
    />
  )
}

function SidebarGroup({ className, ...props }) {
  const { themeObject } = useTheme();
  const groupTokens = themeObject.sidebarTokens?.group || {};
  
  return (
    <div
      className={cn("relative flex w-full min-w-0 flex-col p-2", className)}
      style={{
        display: groupTokens.display,
        width: groupTokens.width,
        minWidth: groupTokens.minWidth,
        flexDirection: groupTokens.flexDirection,
        padding: groupTokens.padding,
      }}
      {...props}
    />
  )
}

function SidebarGroupLabel({
  className,
  asChild = false,
  ...props
}) {
  const { themeObject } = useTheme();
  const groupLabelTokens = themeObject.sidebarTokens?.groupLabel || {};
  
  const Comp = asChild ? Slot : "div"

  return (
    <Comp
      className={cn(
        "text-white/35 flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-semibold uppercase tracking-widest transition-[margin,opacity] duration-200 ease-linear [&>svg]:size-4 [&>svg]:shrink-0",
        "group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0",
        className
      )}
      style={{
        height: groupLabelTokens.height,
        flexShrink: groupLabelTokens.flexShrink,
        display: groupLabelTokens.display,
        alignItems: groupLabelTokens.alignItems,
        borderRadius: groupLabelTokens.borderRadius,
        paddingLeft: groupLabelTokens.paddingLeft,
        paddingRight: groupLabelTokens.paddingRight,
        fontSize: groupLabelTokens.fontSize,
        fontWeight: groupLabelTokens.fontWeight,
        transition: groupLabelTokens.transition,
      }}
      {...props}
    />
  )
}

function SidebarGroupContent({ className, ...props }) {
  const { themeObject } = useTheme();
  const groupContentTokens = themeObject.sidebarTokens?.groupContent || {};
  
  return (
    <div
      className={cn("w-full text-sm", className)}
      style={{
        fontSize: groupContentTokens.fontSize,
        gap: groupContentTokens.gap,
      }}
      {...props}
    />
  )
}

function SidebarMenu({ className, ...props }) {
  const { themeObject } = useTheme();
  const menuTokens = themeObject.sidebarTokens?.menu || {};
  
  return (
    <ul
      className={cn("flex w-full min-w-0 flex-col gap-1", className)}
      style={{
        display: menuTokens.display,
        minWidth: menuTokens.minWidth,
        flexDirection: menuTokens.flexDirection,
        gap: menuTokens.gap,
      }}
      {...props}
    />
  )
}

function SidebarMenuItem({ className, ...props }) {
  const { themeObject } = useTheme();
  const menuItemTokens = themeObject.sidebarTokens?.menuItem || {};
  
  return (
    <li
      className={cn("group/menu-item relative", className)}
      style={{
        position: menuItemTokens.position,
      }}
      {...props}
    />
  )
}

const sidebarMenuButtonVariants = cva(
  "peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm transition-[width,height,padding]",
  {
    variants: {
      variant: {
        default: cn(
          "text-white/60 transition-all duration-150",
          "hover:bg-white/10 hover:text-white",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring focus-visible:ring-offset-2",
          "active:bg-white/15 active:text-white",
          "disabled:pointer-events-none disabled:opacity-40",
          "data-[active=true]:bg-white/10 data-[active=true]:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.10)] data-[active=true]:text-white data-[active=true]:font-semibold"
        ),
        outline: cn(
          "bg-white/5 shadow-sm border border-white/10",
          "hover:bg-white/10 hover:text-white",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring"
        ),
      },
      size: {
        default: "h-10 text-sm",
        sm: "h-7 text-xs",
        lg: "h-12 text-sm group-data-[collapsible=icon]:p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function SidebarMenuButton({
  asChild = false,
  isActive = false,
  variant = "default",
  size = "default",
  className,
  ...props
}) {
  const { themeObject } = useTheme();
  const menuButtonTokens = themeObject.sidebarTokens?.menuButton || {};
  
  const Comp = asChild ? Slot : "button"

  const getHeightForSize = (size) => {
    if (size === 'sm') return menuButtonTokens.height?.sm;
    if (size === 'lg') return menuButtonTokens.height?.lg;
    return menuButtonTokens.height?.md;
  };

  return (
    <Comp
      data-active={isActive}
      className={cn(sidebarMenuButtonVariants({ variant, size }), className)}
      style={{
        display: menuButtonTokens.display,
        width: menuButtonTokens.width,
        minWidth: menuButtonTokens.minWidth,
        alignItems: menuButtonTokens.alignItems,
        gap: menuButtonTokens.gap,
        overflow: menuButtonTokens.overflow,
        borderRadius: menuButtonTokens.borderRadius,
        padding: menuButtonTokens.padding,
        textAlign: menuButtonTokens.textAlign,
        fontSize: menuButtonTokens.fontSize,
        fontWeight: menuButtonTokens.fontWeight,
        transition: menuButtonTokens.transition,
        cursor: menuButtonTokens.cursor,
        backgroundColor: variant === 'outline' ? menuButtonTokens.backgroundColor : undefined,
        boxShadow: variant === 'outline' ? menuButtonTokens.boxShadow : undefined,
        border: variant === 'outline' ? menuButtonTokens.border : undefined,
        height: getHeightForSize(size),
      }}
      {...props}
    />
  )
}

export {
  Sidebar,
  SidebarProvider,
  SidebarTrigger,
  SidebarInset,
  SidebarInput,
  SidebarHeader,
  SidebarFooter,
  SidebarSeparator,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  useSidebar,
}
